import { CompanyProfile, CompanyProfileDto, EnhancedCompanyProfileDto } from '@mkp/company/models';
import { mapLocationDtoToLocation } from '@mkp/location/data-access';
import { EMPLOYEE_COUNT_RANGE, MinMax } from '@mkp/metadata/data-access';
import { LegalEntityState } from '@mkp/shared/models';

export const mapEnhancedCompanyDtoToCompanyProfile = (
  dto: EnhancedCompanyProfileDto
): CompanyProfile => {
  const { employeeCountFrom, employeeCountTo } = mapEmployeeCount(
    dto.employeeCountFrom,
    dto.employeeCountTo
  );

  const mappedLocations = dto._embedded.locations.map(mapLocationDtoToLocation);

  return {
    id: dto.id,
    name: dto.name,
    legalEntityId: dto.legalEntityId,
    legalEntityName: dto.legalEntityName,
    legalEntityAddress: dto.legalEntityAddress,
    legalEntityState: isValidState(dto.legalEntityState) ? dto.legalEntityState : undefined,
    legalEntityVersion: dto.legalEntityVersion,
    locationId: dto.locationId,
    headerMediaApiId: dto.headerMediaApiId ?? null,
    logoMediaApiId: dto.logoMediaApiId ?? null,
    description: dto.description,
    employeeCountFrom: dto.employeeCountFrom,
    employeeCountTo: dto.employeeCountTo,
    employerBranding: dto.employerBranding ?? null,
    benefits: dto.benefits,
    links: dto.links,
    videos: dto.videos,
    companySize: `${employeeCountFrom}-${employeeCountTo}`,
    _version: dto._version,
    locations: mappedLocations,
    _embedded: { locations: mappedLocations },
    _links: dto._links,
  };
};

export const mapCompanyProfileToCompanyRequestBody = (
  profile: Partial<CompanyProfile> & Pick<CompanyProfile, 'id' | '_version'>
): Partial<CompanyProfileDto> => {
  return {
    id: profile.id,
    name: profile.name,
    legalEntityId: profile.legalEntityId,
    locationId: profile.locationId,
    headerMediaApiId: profile.headerMediaApiId,
    logoMediaApiId: profile.logoMediaApiId,
    description: profile.description,
    employeeCountFrom: profile.employeeCountFrom,
    employeeCountTo: profile.employeeCountTo,
    employerBranding: profile.employerBranding,
    benefits: profile.benefits,
    links: profile.links,
    videos: profile.videos,
    _version: profile._version,
  };
};

export const isValidState = (state: unknown): state is LegalEntityState =>
  Object.values(LegalEntityState).includes(state as LegalEntityState);

export const mapEmployeeCount = (
  minInput: number,
  maxInput: number
): Pick<CompanyProfile, 'employeeCountFrom' | 'employeeCountTo'> => {
  const { min: closestMin, max: backupMax } = getClosestRange(minInput);
  const { max: closestMax } = getClosestRange(maxInput);

  return {
    employeeCountFrom: closestMin,
    // use backupMax if max is somehow lower than min
    employeeCountTo: closestMax > closestMin ? closestMax : backupMax,
  };
};

export const getClosestRange = (value: number): MinMax =>
  EMPLOYEE_COUNT_RANGE.find(({ max }) => value <= max) ?? EMPLOYEE_COUNT_RANGE[0];
